import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Building2, Save, Image as ImageIcon, Banknote, Hash, MailCheck } from "lucide-react";

/**
 * Phase 5J — Company Settings (Payslip Compliance)
 *
 * Surface the legally-mandated fields shown on every payslip per the
 * Code on Wages Central Rules 2026: company name, address, CIN, PAN,
 * GSTIN, PF & ESI establishment codes, HR contact email, plus logo URL
 * and company bank for payment references.
 */
export default function CompanySettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company-settings");
      setForm(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load company settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Send only the editable fields
      const payload = {
        name: form.name, address: form.address,
        cin: form.cin, pan: form.pan, gstin: form.gstin,
        pf_code: form.pf_code, esi_code: form.esi_code,
        hr_email: form.hr_email, logo_url: form.logo_url,
        bank_name: form.bank_name, bank_account: form.bank_account,
      };
      const { data } = await api.put("/company-settings", payload);
      setForm({ ...form, ...data });
      toast.success("Company settings saved — payslips will use these from next run.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="max-w-4xl space-y-3 animate-pulse">
        <div className="h-8 w-64 bg-slate-200 rounded"></div>
        <div className="h-32 bg-slate-100 rounded"></div>
        <div className="h-32 bg-slate-100 rounded"></div>
      </div>
    );
  }

  const f = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className="max-w-4xl space-y-4" data-testid="company-settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Building2 className="h-3 w-3" /> Administration · Phase 5J
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Company Settings
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">
          These fields appear on every payslip header per Code on Wages Central Rules 2026.
        </p>
      </div>

      {/* Identity */}
      <SettingsCard
        title="Company Identity"
        icon={<Building2 className="h-4 w-4 text-red-700" />}
        accent="pink"
      >
        <Field label="Company Name *" testid="name-input"
                value={form.name} onChange={f("name")} />
        <Field label="Registered Address" testid="address-input" multiline
                value={form.address} onChange={f("address")} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="CIN (Company Identification Number)" testid="cin-input"
                  value={form.cin} onChange={f("cin")}
                  hint="21-char Corporate Identification Number from MCA" />
          <Field label="Company PAN" testid="pan-input"
                  value={form.pan} onChange={f("pan")}
                  hint="e.g. AAACB1234R" />
          <Field label="GSTIN" testid="gstin-input"
                  value={form.gstin} onChange={f("gstin")}
                  hint="15-char GST Identification Number" />
          <Field label="HR Email" testid="hr-email-input"
                  value={form.hr_email} onChange={f("hr_email")}
                  hint="Shown in payslip footer for employee queries" />
        </div>
      </SettingsCard>

      {/* Statutory Registrations */}
      <SettingsCard
        title="Statutory Registration Codes"
        icon={<Hash className="h-4 w-4 text-amber-700" />}
        accent="amber"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="PF Establishment Code" testid="pf-code-input"
                  value={form.pf_code} onChange={f("pf_code")}
                  hint="Format: RGN/OFC/0001234/000 (from EPFO portal)" />
          <Field label="ESI Establishment Code" testid="esi-code-input"
                  value={form.esi_code} onChange={f("esi_code")}
                  hint="17-digit ESI factory code" />
        </div>
      </SettingsCard>

      {/* Bank */}
      <SettingsCard
        title="Company Bank (for payment references)"
        icon={<Banknote className="h-4 w-4 text-emerald-700" />}
        accent="emerald"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Bank Name" testid="bank-name-input"
                  value={form.bank_name} onChange={f("bank_name")} />
          <Field label="Bank Account Number" testid="bank-account-input"
                  value={form.bank_account} onChange={f("bank_account")} />
        </div>
      </SettingsCard>

      {/* Logo */}
      <SettingsCard
        title="Company Logo"
        icon={<ImageIcon className="h-4 w-4 text-indigo-700" />}
        accent="indigo"
      >
        <Field label="Logo URL (public)" testid="logo-url-input"
                value={form.logo_url} onChange={f("logo_url")}
                hint="Square / wide PNG-JPG accessible without auth. Used on payslip headers." />
        {form.logo_url && (
          <div className="mt-2 inline-block bg-white border border-slate-200 rounded p-2">
            <img src={form.logo_url} alt="Company logo preview"
                  className="max-h-16 max-w-[200px] object-contain"
                  onError={(e) => { e.target.style.display = "none"; }}
                  data-testid="logo-preview" />
          </div>
        )}
      </SettingsCard>

      <div className="sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-4 -mt-2 z-10">
        <div className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <MailCheck className="h-3.5 w-3.5 text-emerald-600" />
            {form.updated_at ? (
              <span>Last updated <strong>{new Date(form.updated_at).toLocaleString()}</strong>{form.updated_by ? ` by ${form.updated_by}` : ""}</span>
            ) : (
              <span>No prior changes recorded.</span>
            )}
          </div>
          <Button
            onClick={save} disabled={saving}
            className="brand-bg brand-bg-hover text-white"
            data-testid="save-btn"
          >
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? "Saving…" : "Save All"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SettingsCard({ title, icon, accent = "slate", children }) {
  const accents = {
    pink: "border-red-200 from-red-50 to-white",
    amber: "border-amber-200 from-amber-50 to-white",
    emerald: "border-emerald-200 from-emerald-50 to-white",
    indigo: "border-indigo-200 from-indigo-50 to-white",
    slate: "border-slate-200 from-slate-50 to-white",
  };
  return (
    <div className={`border bg-gradient-to-br ${accents[accent]} rounded-lg overflow-hidden`}>
      <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
        {icon}
        <div className="text-xs font-bold uppercase tracking-wider text-slate-800">{title}</div>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, hint, testid, multiline = false }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">{label}</Label>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={onChange}
          rows={2}
          className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-400 resize-y"
          data-testid={testid}
        />
      ) : (
        <Input value={value || ""} onChange={onChange} className="h-9 text-sm" data-testid={testid} />
      )}
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
